import React, { useState, useEffect } from "react";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Input, Row, Typography, Card, Table, Spin, Select, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/layouts/MainLayout";
import { connect, useDispatch } from "react-redux";
import { apiConfig, labelConfig, staticConfig } from "../teller.config";
import { rightAction } from "../../../redux/actions";
import TopContent from "../../../components/layouts/TopContent";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import CustomPagination from "../../../components/CustomPagination";

const { Text } = Typography;

const ManageTellerTransactions = (props) => {
  const navigate = useNavigate();
  const { rightBtn, userData } = props;
  const [transactionList, setTransactionList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tableCount, setTableCount] = useState("10");
  const [selectedRow, setSelectedRow] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const dispatch = useDispatch();

  const mapTransactionRow = (item) => ({
    key: item?.id,
    id: item?.id,
    transactionDate: BOM.ReformatDate(item?.transactionDate, "DD MMM YYYY"),
    tellerName: item?.teller?.description,
    amount: item?.amount?.toLocaleString(),
    currency: item?.currencyCode,
    branch: item?.office?.name,
    transactionDescription:
      item?.txnType === "REQ"
        ? "Request Cash From Vault"
        : item?.txnType === "SND"
          ? "Send Cash to Vault"
          : item?.txnType === "WTH"
            ? "Teller Cash Withdrawal"
            : item?.txnType === "DEP"
              ? "Teller Cash Deposit"
              : item?.txnType === "CLO"
                ? "Teller Batch Closure"
                : "-",
    transactionStatus: item?.status === 2 ? "Received" : "Unprocessed",
    tillBatchId: item?.tillBatchId,
    txnType: item?.txnType,
    txnNote: item?.txnNote,
  });

  const columns = [
    {
      title: "Transaction Date",
      dataIndex: "transactionDate",
    },
    {
      title: "Teller Name",
      dataIndex: "tellerName",
    },
    {
      title: "Amount",
      render: (_, record) => <Text>{BOM.FormatAmount(record?.amount)}</Text>,
      dataIndex: "amount",
    },
    {
      title: "Currency",
      dataIndex: "currency",
    },
    {
      title: "Branch",
      dataIndex: "branch",
    },
    {
      title: "Transaction Description",
      dataIndex: "transactionDescription",
    },
    {
      title: "Transaction Status",
      dataIndex: "transactionStatus",
      render: (_, record) => (
        <Text>{handleFlgStatus(record?.transactionStatus, {}, true)}</Text>
      ),
    },
  ];

  const handleFlgStatus = (status, style = {}, isListing = false) => {
    if (!status) {
      return (
        <Tag
          icon={<ClockCircleOutlined />}
          color={`${isListing ? "warning" : "#FFEB86"}`}
          style={{ color: "black", ...style }}
        >
          -
        </Tag>
      );
    }

    switch (status.toLowerCase()) {
      case "completed":
      case "received":
        return (
          <Tag
            icon={<CheckCircleOutlined />}
            color={`${isListing ? "success" : "#87D068"}`}
            style={style}
          >
            {status}
          </Tag>
        );
      case "rejected":
      case "failed":
      case "closed":
        return (
          <Tag
            icon={<CloseCircleOutlined />}
            color={`${isListing ? "red" : "#FA0008"}`}
            style={style}
          >
            {status}
          </Tag>
        );
      default:
        return (
          <Tag
            icon={<ClockCircleOutlined />}
            color={`${isListing ? "warning" : "#FFEB86"}`}
            style={{ color: "black", ...style }}
          >
            {BOM.SubstringFormat(status, 21)}
          </Tag>
        );
    }
  };

  function handleSearchInTable(e) {
    const searchKey = e.target.value?.toLowerCase();

    if (!searchKey) {
      setIsSearchMode(false);
      setCurrentPage(1);
      getTemplate(0, Number(tableCount));
      return;
    }

    setIsSearchMode(true);
    searchAllTransactions(searchKey);
  }

  function selectTableCount(value) {
    setTableCount(value);
    setCurrentPage(1);
    onRefresh(0, Number(value));
  }

  const onRefresh = (offset = 0, limit = Number(tableCount)) => {
    getTemplate(offset, limit);
  };

  const getTemplate = (offset = 0, limit = Number(tableCount)) => {
    let body = {
      session: userData.session,
      params: { offset, limit },
    };

    const url = `${apiConfig.Transaction}?officeId=${userData?.officeId}`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        setIsLoading(false);
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        const objList = res?.pageItems || [];
        setTotalItems(res?.totalFilteredRecords ?? 0);
        const tempArray = objList.map(mapTransactionRow);

        setTransactionList(tempArray);

        //STOP LOAD
        setIsLoading(false);
      }
    });
  };

  const matchesSearch = (item, searchKey) =>
    String(item?.transactionDate ?? "")
      .toLowerCase()
      .includes(searchKey) ||
    String(item?.tellerName ?? "")
      .toLowerCase()
      .includes(searchKey) ||
    String(item?.amount ?? "")
      .toLowerCase()
      .includes(searchKey) ||
    String(item?.currency ?? "")
      .toLowerCase()
      .includes(searchKey) ||
    String(item?.branch ?? "")
      .toLowerCase()
      .includes(searchKey) ||
    String(item?.transactionDescription ?? "")
      .toLowerCase()
      .includes(searchKey) ||
    String(item?.transactionStatus ?? "")
      .toLowerCase()
      .includes(searchKey);

  const searchAllTransactions = (searchKey) => {
    let body = {
      session: userData.session,
      params: { offset: 0, limit: totalItems > 0 ? totalItems : 100000 },
    };

    const url = `${apiConfig.Transaction}?officeId=${userData?.officeId}`;

    setIsLoading(true);
    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        setIsLoading(false);
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
      } else {
        const objList = res?.pageItems || [];
        const tempArray = objList.map(mapTransactionRow);

        setTransactionList(
          tempArray.filter((item) => matchesSearch(item, searchKey)),
        );
        setIsLoading(false);
      }
    });
  };

  useEffect(() => {
    BOM.HasPermission("RECEIVE_TELLER_TRANSACTION") && getTemplate();
  }, [dispatch]);

  function handleOnView() {
    const { detailBtn } = rightBtn;
    if (detailBtn?.toLowerCase() === "primary" && selectedRow) {
      navigate("/receive-teller-transaction", {
        state: {
          id: selectedRow.id,
          transactionDate: selectedRow.transactionDate,
          description: selectedRow.transactionDescription,
          tellerName: selectedRow.tellerName,
          amount: selectedRow.amount,
          currencyCode: selectedRow.currency,
          transactionStatus: selectedRow.transactionStatus,
          tillBatchId: selectedRow.tillBatchId,
          txnType: selectedRow.txnType,
          txnNote: selectedRow.txnNote,
        },
      });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  const onSelectItemAction = (selectedRowKeys, objParam) => {
    console.log(objParam);
    setSelectedRow(objParam[0]);
    console.log(selectedRowKeys);
    console.log(selectedRow);

    [
      BOM.HasPermission("RECEIVE_TELLER_TRANSACTION") && cnt.RIGHT_DETAIL_BTN,
    ].forEach((item) => item && dispatch({ type: item }));
  };

  return (
    <MainLayout isEnableRightSidebar={true} onView={handleOnView}>
      <TopContent
        pageTitle={labelConfig.manageTellerTransactions}
        pageItems={staticConfig.manageTellerTransactionsItems}
      />

      <Card
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: "auto" }}>
              <Text style={{ marginRight: 8 }}>Show</Text>
              <Select
                defaultValue={tableCount}
                options={[
                  { value: "5", label: "5" },
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                ]}
                onSelect={selectTableCount}
              />
              <Text style={{ marginLeft: 8 }}>Entries</Text>
            </div>

            <div style={{ margin: "0px 50px" }}>
              <Input
                onChange={handleSearchInTable}
                placeholder="Filter by teller, branch, description, status..."
                style={{
                  width: 400,
                }}
              />
            </div>
          </Row>
          <Table
            rowSelection={{
              type: "radio",
              onChange: onSelectItemAction,
              hideSelectAll: true,
            }}
            columns={columns}
            dataSource={transactionList}
            bordered
            pagination={false}
          />
          {!isSearchMode && (
            <CustomPagination
              totalItems={totalItems}
              pageSize={Number(tableCount)}
              onPageChange={(page, offset) => onRefresh(offset)}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ManageTellerTransactions);
